import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionSearchFunction,
  TypesenseCollection,
} from '../types';
import { searchCollection, TypesenseDocument } from '../utils';
import { Employee, EmployeeOption } from './employees.types';

const BASE_FILTER_ACTIVE = 'Employed:true';

export const formatEmployeeDisplayName = (name: string, initials: string) => {
  return initials?.length ? `${name} (${initials})` : name;
};

export const getSearchEmployeeSelectOptions: SelectOptionSearchFunction<
  EmployeeOption
> = async (
  searchValue: string,
  apiKey: string,
  pageIndex: number = 1,
  options: SearchOptions = {},
) => {
  const results = await searchCollection(
    TypesenseCollection.EMPLOYEES_VIEW.name,
    'Name,Initials',
    searchValue,
    apiKey,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    options.filterBy
      ? `${options.filterBy} && ${BASE_FILTER_ACTIVE}`
      : BASE_FILTER_ACTIVE,
    options.sortBy ? `${options.sortBy}` : 'Name:asc',
  );
  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch employees from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: formatEmployeeDisplayName(item.Name, item.Initials),
    id: item.SourceId.toString(),
    legalEntityCurrencyIsoCode: item.LegalEntityCurrencyCode,
    legalEntityId: item.LegalEntityId,
  }));
  return mappedResults ?? [];
};

/**
 * Retrieves a employee select option by ID from Typesense.
 * @throws {Error} When the search fails, returns an error, or no employee is found with the given ID
 */
export const getEmployeeSelectOptionById: GetSelectOptionByIdFunction<
  EmployeeOption
> = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.EMPLOYEES_VIEW.name,
    'Name',
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `${additionalFilterOptions} && SourceId:=${id}`
      : `SourceId:=${id}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch employee by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: formatEmployeeDisplayName(item.Name, item.Initials),
    id: item.SourceId.toString(),
    legalEntityCurrencyIsoCode: item.LegalEntityCurrencyCode,
    legalEntityId: item.LegalEntityId,
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`Employee with id ${id} not found`);
    throw new Error(`Employee with id ${id} not found`);
  }

  return mappedResults[0];
};

export const getEmployeeById = async (
  sourceId: string | null | undefined,
  collectionApiKey: string | null | undefined,
): Promise<Employee | null> => {
  if (!sourceId || !collectionApiKey) return null;

  const results = await searchCollection(
    TypesenseCollection.EMPLOYEES.name,
    'Name',
    '*',
    collectionApiKey,
    1,
    1,
    `SourceId:=${sourceId}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch employee by id from Typesense',
    );
  }

  if (!results.documents || results.documents.length === 0) {
    console.debug(`Employee with SourceId ${sourceId} not found`);
    return null;
  }

  return results.documents[0] as Employee;
};
